import type { KBEquipmentSummary, KBSensorSummary } from "./api/agent";

/**
 * Tietopohja Kenttälaitteet grouping — the knowledge-base catalog list.
 * Konfigurointi Tyyppi uses these same groups and glyphs so the two UIs
 * do not invent a second taxonomy (equipment YAML `category_fi`).
 */
export const FIELD_DEVICE_GROUPS: {
  class: KBSensorSummary["device_class"];
  label: string;
  icon: string;
}[] = [
  { class: "meter", label: "Mittarit", icon: "🔢" },
  { class: "sensor", label: "Anturit", icon: "📡" },
  { class: "actuator", label: "Toimilaitteet", icon: "🔘" },
  { class: "drive", label: "Taajuusmuuttajat", icon: "⚙️" },
];

export const FIELD_DEVICE_ICON: Record<
  KBSensorSummary["device_class"],
  string
> = {
  meter: "🔢",
  sensor: "📡",
  actuator: "🔘",
  drive: "⚙️",
};

/** Fallback when `GET /kb/equipment-type-meta` has no icon for the type yet. */
export const FALLBACK_EQUIPMENT_TYPE_ICON = "⚙️";

/** Sensor catalog id → equipment folder for the combined MeterDetail view. */
export const METER_EQUIPMENT_TYPE: Record<string, string> = {
  "electricity-meter": "electricity-meter",
  "heat-meter": "heat-meter",
  "water-meter": "water-meter",
};

/** Meter-class Kenttälaitteet equipment folders (Mittarit). */
const METER_FIELD_DEVICE_TYPES = new Set(Object.values(METER_EQUIPMENT_TYPE));

/** Equipment types shown only under Kenttälaitteet, not Talotekniikka. */
export const FIELD_DEVICE_EQUIPMENT_TYPES = new Set<string>([
  ...Object.values(METER_EQUIPMENT_TYPE),
  "temperature-sensor",
  "people-counter",
  "co2-sensor",
]);

export interface EquipmentTypeOption {
  type: string;
  name_fi: string;
  icon: string;
}

export interface EquipmentTypeGroup {
  group_fi: string;
  options: EquipmentTypeOption[];
}

/** The picker option that carries KB `name_fi` for a stored type, if listed. */
export function findEquipmentTypeOption(
  groups: EquipmentTypeGroup[],
  equipmentType: string | null | undefined,
): EquipmentTypeOption | undefined {
  if (!equipmentType) return undefined;
  for (const group of groups) {
    const option = group.options.find((o) => o.type === equipmentType);
    if (option) return option;
  }
  return undefined;
}

/**
 * Finnish type label for a stored `equipment_type`. Same KB `name_fi` as the
 * Tyyppi picker: equipment-type-meta for HVAC/meters, sensor catalog for
 * Anturit field-device ids (e.g. `people-counter`). Unknown slugs stay as the slug.
 */
export function equipmentTypeNameFi(
  eqMeta: Record<string, KBEquipmentSummary>,
  equipmentType: string | null | undefined,
  sensors: readonly KBSensorSummary[] = [],
): string {
  if (!equipmentType) return "";
  const listed = findEquipmentTypeOption(
    groupedEquipmentTypeOptions(eqMeta, sensors),
    equipmentType,
  );
  return listed?.name_fi ?? equipmentType;
}

/**
 * Icon Tietopohja shows for an equipment type — the shared catalog symbol.
 * Kenttälaitteet (Mittarit/Anturit) use the group glyph; Talotekniikka uses
 * YAML `icon` from `GET /kb/equipment-type-meta`. Panels must not invent a
 * third source (stored `equipment.icon`, Lucide wrench, …).
 */
export function equipmentTypeIcon(
  eqMeta: Record<string, KBEquipmentSummary>,
  equipmentType: string | null | undefined,
  sensors: readonly KBSensorSummary[] = [],
): string {
  if (!equipmentType) return FALLBACK_EQUIPMENT_TYPE_ICON;
  if (METER_FIELD_DEVICE_TYPES.has(equipmentType)) {
    return FIELD_DEVICE_ICON.meter;
  }
  if (FIELD_DEVICE_EQUIPMENT_TYPES.has(equipmentType)) {
    return FIELD_DEVICE_ICON.sensor;
  }
  const sensor = sensors.find((s) => s.id === equipmentType);
  if (sensor) return FIELD_DEVICE_ICON[sensor.device_class];
  return eqMeta[equipmentType]?.icon || FALLBACK_EQUIPMENT_TYPE_ICON;
}

function equipmentTypeForSensor(
  sensorId: string,
  eqMeta: Record<string, KBEquipmentSummary>,
): string | undefined {
  // Only the explicit Tietopohja meter map. A sensor whose id
  // happens to equal an equipment folder is *not* a field-device mapping —
  // that stole Talotekniikka types into Kenttälaitteet and, together with
  // excluding FIELD_DEVICE_EQUIPMENT_TYPES from Talotekniikka, could drop
  // leftover field-device folders from both sides of the picker.
  const mapped = METER_EQUIPMENT_TYPE[sensorId];
  if (mapped && eqMeta[mapped]) return mapped;
  return undefined;
}

function isTalotekniikkaFolder(
  eqType: string,
  eqMeta: Record<string, KBEquipmentSummary>,
): boolean {
  return Boolean(eqMeta[eqType]) && !FIELD_DEVICE_EQUIPMENT_TYPES.has(eqType);
}

function leftoverFieldDeviceBelongsToGroup(
  eqType: string,
  cls: KBSensorSummary["device_class"],
): boolean {
  if (cls === "meter") return METER_FIELD_DEVICE_TYPES.has(eqType);
  if (cls === "sensor") return !METER_FIELD_DEVICE_TYPES.has(eqType);
  return false;
}

/**
 * Equipment-type picker groups matching Tietopohja: Talotekniikka (YAML
 * icons) plus Kenttälaitteet subgroups (Anturit/Mittarit/… group glyphs).
 * Every `eqMeta` key appears exactly once. Sensor-only catalog ids (e.g.
 * `co2-sensor`) are listed under Anturit so a stored field-device row
 * can keep that type without a duplicate equipment folder. `people-counter`
 * is an installable Kenttälaitteet folder (same as `temperature-sensor`).
 */
export function groupedEquipmentTypeOptions(
  eqMeta: Record<string, KBEquipmentSummary>,
  sensors: readonly KBSensorSummary[],
): EquipmentTypeGroup[] {
  const placed = new Set<string>();
  const fieldGroups: EquipmentTypeGroup[] = [];

  for (const { class: cls, label } of FIELD_DEVICE_GROUPS) {
    const options: EquipmentTypeOption[] = [];
    for (const sensor of sensors) {
      if (sensor.device_class !== cls) continue;
      const mapped = equipmentTypeForSensor(sensor.id, eqMeta);
      const typeId = mapped ?? sensor.id;
      if (placed.has(typeId)) continue;
      if (!mapped && isTalotekniikkaFolder(typeId, eqMeta)) continue;
      placed.add(typeId);
      options.push({
        type: typeId,
        name_fi: eqMeta[typeId]?.name_fi ?? sensor.name_fi,
        icon: equipmentTypeIcon(eqMeta, typeId, sensors),
      });
    }
    for (const eqType of FIELD_DEVICE_EQUIPMENT_TYPES) {
      if (placed.has(eqType) || !eqMeta[eqType]) continue;
      if (!leftoverFieldDeviceBelongsToGroup(eqType, cls)) continue;
      placed.add(eqType);
      const summary = eqMeta[eqType];
      options.push({
        type: eqType,
        name_fi: summary.name_fi,
        icon: equipmentTypeIcon(eqMeta, eqType, sensors),
      });
    }
    if (options.length === 0) continue;
    options.sort((a, b) => a.name_fi.localeCompare(b.name_fi, "fi"));
    fieldGroups.push({ group_fi: label, options });
  }

  const talotekniikka = Object.entries(eqMeta)
    .filter(([type]) => !placed.has(type))
    .map(([type, summary]) => ({
      type,
      name_fi: summary.name_fi,
      icon: equipmentTypeIcon(eqMeta, type, sensors),
    }))
    .sort((a, b) => a.name_fi.localeCompare(b.name_fi, "fi"));

  const groups: EquipmentTypeGroup[] = [];
  if (talotekniikka.length > 0) {
    groups.push({ group_fi: "Talotekniikka", options: talotekniikka });
  }
  groups.push(...fieldGroups);
  return groups;
}
